import { usePage } from '@inertiajs/inertia-vue3';
import { ref } from 'vue';

const Typesense = require('typesense');

export function useTypesenseOffers() {
    const offers = ref([]);
    const page = ref(1);
    const query = ref('');
    const lat = ref('');
    const lng = ref('');
    const companySlug = ref('');

    const canLoadMore = ref(false);
    const loading = ref(false);

    let clientConfigured = false;
    let client = null;

    // When we're on the first page and not filtering, insert a few external
    // offers (scraping and XML) in the middle of the search results
    const numberOfCompanies = 10;
    const numberOfOffersPerCompany = 3;
    const externalOffersOffset = 15;

    try {
        const config = usePage().props.value.typesense;

        client = new Typesense.Client({
            nodes: [
                {
                    host: config.host,
                    port: config.port,
                    protocol: config.protocol,
                },
            ],
            apiKey: config.search_key,
            connectionTimeoutSeconds: 10,
        });

        clientConfigured = true;
    } catch (e) {
        console.log('Typesense client not configured', e);
    }

    async function search(newQuery = '', newLat = '', newLng = '', newPage = 1, newCompanySlug = '') {
        query.value = newQuery;
        lat.value = newLat;
        lng.value = newLng;
        page.value = newPage;
        companySlug.value = newCompanySlug;

        offers.value = await getOffers();
    }

    async function loadMore() {
        page.value++;
        let newOffers = await getOffers();
        offers.value = [...offers.value, ...newOffers];
    }

    async function getOffers() {
        if (!clientConfigured) {
            return [];
        }

        loading.value = true;

        let perPage = 250;
        let externalOffers = [];

        if (page.value == 1 && !query.value.length && !lat.value && !lng.value && !companySlug.value) {
            externalOffers = await getExternalOffers();
            perPage -= externalOffers.length;
        }

        let searchParameters = {
            q: query.value,
            query_by: 'title_fr,title_en,company_name,description_fr,description_en',
            num_typos: 2,
            typo_tokens_threshold: 10,
            sort_by: '',
            page: page.value,
            per_page: perPage,
            exhaustive_search: true,
        };

        let sort_by =
            lat.value && lng.value
                ? `location(${lat.value}, ${lng.value}):asc,_eval(is_promoted:true),updated_at:desc`
                : `_eval(is_promoted:true),updated_at:desc`;

        searchParameters.sort_by = sort_by;

        let filter_by = `is_active:true`;

        // Exclude XML and scraper offers from the "all" view
        // UNLESS they are promoted or prime time offers
        // - but they should still be searchable
        if (query.value.length == 0 && !companySlug.value && !lat.value && !lng.value) {
            filter_by += ' && ((is_xml:false && is_scraped:false) || (is_promoted:true || is_prime_time:true))';
        }

        // Website
        // Website - CAMAQ = 63
        filter_by += ' && websites:63';

        // Language
        let language = usePage().props.value.locale;
        filter_by += ` && (language:${language} || language:bi)`;

        // Company
        if (companySlug.value) {
            filter_by += ` && company_slug:${companySlug.value}`;
        }

        searchParameters.filter_by = filter_by;

        let indexName = usePage().props.value.typesense.offers_index_name;
        let result = await client.collections(indexName).documents().search(searchParameters);

        // Use the original 250 value for the pagination here
        // If we loaded a lot of external offers it might be bugged
        canLoadMore.value = page.value * 250 < result.found;
        loading.value = false;

        let offers = result.hits.map(hit => {
            return hit.document;
        });

        // If we have external offers, then insert them after the first 15 offers
        if (externalOffers.length > 0) {
            let first15Offers = offers.slice(0, externalOffersOffset);
            let remainingOffers = offers.slice(15);

            offers = [...first15Offers, ...externalOffers, ...remainingOffers];
        }

        return offers;
    }

    async function getExternalOffers() {
        let searchParameters = {
            q: query.value,
            query_by: 'title_fr,title_en,company_name,description_fr,description_en',
            num_typos: 2,
            typo_tokens_threshold: 10,
            sort_by: 'updated_at:desc',
            page: page.value,
            per_page: numberOfCompanies,
            group_by: 'company_name',
            group_limit: numberOfOffersPerCompany,
        };

        let filter_by = `is_active:true`;

        // Website
        // Website - CAMAQ = 63
        filter_by += ' && websites:63';

        // Language
        let language = usePage().props.value.locale;
        filter_by += ` && (language:${language} || language:bi)`;

        // XML and scraping only
        filter_by += ` && (is_xml:true || is_scraped:true)`;

        searchParameters.filter_by = filter_by;

        let indexName = usePage().props.value.typesense.offers_index_name;
        let result = await client.collections(indexName).documents().search(searchParameters);

        return result.grouped_hits
            .map(group => {
                return group.hits.map(hit => {
                    return hit.document;
                });
            })
            .flat();
    }

    return {
        offers,
        search,
        loadMore,
        canLoadMore,
        loading,
    };
}
